import { GET_PROPERTY_INFO, useUpdatePropertyStatus } from '@securecore-new-application/securecore-datacore';
import { Property, PropertyStatuses } from '@securecore-new-application/securecore-datacore/lib/types';
import React, { FC, useCallback, useMemo } from 'react';

import { Button } from '../ui/Button';
import Icon, { IconTypes } from '../ui/Icon/Icon';
import { StyledAlert, StyledAlertActions } from './ApproveAlert.styles';

interface Props {
  property: Property;
}

enum AllowedPropertyStatuses {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export const ApproveAlert: FC<Props> = ({ property }) => {
  const { id, status: currentStatus } = property;
  const [updateStatus, { loading }] = useUpdatePropertyStatus();

  const onUpdateStatus = useCallback(
    async (status: AllowedPropertyStatuses) => {
      await updateStatus({
        variables: { data: { propertyId: id, status } },
        refetchQueries: [{ query: GET_PROPERTY_INFO, variables: { id } }],
      });
    },
    [id, updateStatus]
  );
  const message = useMemo(() => {
    switch (currentStatus) {
      case PropertyStatuses.APPROVED:
        return 'This property has been approved by SecureCore team';
      case PropertyStatuses.REJECTED:
        return 'This property has been rejected by SecureCore team';
      default:
        return 'This property is awaiting approval by SecureCore team';
    }
  }, [currentStatus]);

  const iconType = useMemo(() => {
    switch (currentStatus) {
      case PropertyStatuses.APPROVED:
        return IconTypes.NotificationCheck;
      case PropertyStatuses.REJECTED:
        return IconTypes.NotificationAlert;
      default:
        return IconTypes.Time;
    }
  }, [currentStatus]);

  return (
    <StyledAlert
      showIcon
      icon={<Icon icon={iconType} width="30" height="30" />}
      message={message}
      action={
        <StyledAlertActions>
          <Button
            variant="outline"
            size="small"
            onClick={() => onUpdateStatus(AllowedPropertyStatuses.REJECTED)}
            disabled={loading || currentStatus === PropertyStatuses.REJECTED}
          >
            Reject
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={() => onUpdateStatus(AllowedPropertyStatuses.APPROVED)}
            disabled={loading || currentStatus === PropertyStatuses.APPROVED}
          >
            Approve
          </Button>
        </StyledAlertActions>
      }
    />
  );
};
